import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useOrg } from '../../components/OrgLayout'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Card,
  PageHeader,
  Text,
  Button,
  Banner,
  Animate,
  Badge,
  Spinner
} from 'wss3-forge'
import {
  ArrowSync20Regular,
  Checkmark20Regular
} from '@fluentui/react-icons'

// Brand icons (Fluent UI doesn't have these)
const MicrosoftIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
)

const SlackIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" fill="#E01E5A"/>
  </svg>
)

const GoogleIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
)

interface Integration {
  id: string
  provider: 'microsoft' | 'slack' | 'google'
  status: 'active' | 'expired' | 'revoked'
  provider_tenant_id?: string
  provider_workspace_name?: string
  scopes: string[]
  connected_at: string
  last_used_at?: string
}

const PROVIDER_CONFIG = {
  microsoft: {
    name: 'Microsoft Teams',
    description: 'Import contacts from your Azure AD directory',
    Icon: MicrosoftIcon,
    color: '#00A4EF',
    comingSoon: false
  },
  slack: {
    name: 'Slack',
    description: 'Import members from your Slack workspace',
    Icon: SlackIcon,
    color: '#4A154B',
    comingSoon: false
  },
  google: {
    name: 'Google Calendar',
    description: 'Sync calendars for scheduling interviews',
    Icon: GoogleIcon,
    color: '#4285F4',
    comingSoon: true
  }
}

export function OrgIntegrations() {
  const { organization } = useOrg()
  const [searchParams] = useSearchParams()

  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [connecting, setConnecting] = useState<string | null>(null)
  const [disconnecting, setDisconnecting] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    loadIntegrations()

    // Check for OAuth callback result
    const successParam = searchParams.get('success')
    const errorParam = searchParams.get('error')
    const providerParam = searchParams.get('provider')

    if (successParam === 'true' && providerParam) {
      const config = PROVIDER_CONFIG[providerParam as keyof typeof PROVIDER_CONFIG]
      setSuccess(`${config?.name || providerParam} connected successfully`)
      loadIntegrations()
    } else if (errorParam) {
      setError(`Connection failed: ${errorParam}`)
    }
  }, [organization.id])

  const loadIntegrations = async () => {
    try {
      const { data, error: loadError } = await supabase
        .from('organization_integrations')
        .select('*')
        .eq('organization_id', organization.id)

      if (loadError) throw loadError

      setIntegrations(data || [])
    } catch (err: any) {
      console.error('Failed to load integrations:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async (provider: string) => {
    if (PROVIDER_CONFIG[provider as keyof typeof PROVIDER_CONFIG]?.comingSoon) {
      return
    }

    setConnecting(provider)
    setError(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData.session?.access_token

      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/integration-oauth-init`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            organization_id: organization.id,
            provider,
            redirect_uri: window.location.href.split('?')[0]
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start OAuth flow')
      }

      // Redirect to OAuth provider
      window.location.href = data.auth_url

    } catch (err: any) {
      setError(err.message || 'Failed to connect')
      setConnecting(null)
    }
  }

  const handleDisconnect = async (provider: string) => {
    setDisconnecting(provider)
    setError(null)

    try {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData.session?.access_token

      if (!token) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/integration-disconnect`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            organization_id: organization.id,
            provider
          })
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect')
      }

      setSuccess(`${PROVIDER_CONFIG[provider as keyof typeof PROVIDER_CONFIG]?.name || provider} disconnected`)
      await loadIntegrations()

    } catch (err: any) {
      setError(err.message || 'Failed to disconnect')
    } finally {
      setDisconnecting(null)
    }
  }

  const getIntegration = (provider: string) => {
    return integrations.find(i => i.provider === provider)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <VStack gap="lg">
        <PageHeader
          title="Integrations"
          subtitle="Connect external services to enhance your organization's capabilities."
        />
        <VStack gap="md" style={{ alignItems: 'center', padding: '3rem' }}>
          <Spinner size="lg" />
          <Text color="muted">Loading integrations...</Text>
        </VStack>
      </VStack>
    )
  }

  return (
    <VStack gap="lg">
      <Animate type="fadeIn">
        <PageHeader
          title="Integrations"
          subtitle="Connect external services to enhance your organization's capabilities."
        />
      </Animate>

      {error && (
        <Banner variant="error" title="Error" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {success && (
        <Banner variant="success" title="Success" onDismiss={() => setSuccess(null)}>
          {success}
        </Banner>
      )}

      <VStack gap="md">
        {Object.entries(PROVIDER_CONFIG).map(([provider, config], index) => {
          const integration = getIntegration(provider)
          const isConnected = integration?.status === 'active'
          const isExpired = integration?.status === 'expired'

          return (
            <Animate key={provider} type="slideInUp" delay={index * 50}>
              <Card padding="lg">
                <HStack gap="lg" style={{ alignItems: 'flex-start' }}>
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      backgroundColor: isConnected ? `${config.color}15` : 'var(--surface-elevated)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      opacity: config.comingSoon ? 0.5 : 1
                    }}
                  >
                    <config.Icon size={24} />
                  </div>

                  <VStack gap="sm" style={{ flex: 1 }}>
                    <HStack gap="sm" style={{ alignItems: 'center' }}>
                      <Text size="lg" weight="semibold">{config.name}</Text>
                      {config.comingSoon && (
                        <Badge variant="default">Coming Soon</Badge>
                      )}
                      {isConnected && (
                        <Badge variant="success">Connected</Badge>
                      )}
                      {isExpired && (
                        <Badge variant="warning">Expired</Badge>
                      )}
                    </HStack>

                    <Text color="muted">{config.description}</Text>

                    {isConnected && integration && (
                      <VStack gap="xs" style={{ marginTop: '0.5rem' }}>
                        {integration.provider_workspace_name && (
                          <Text size="sm" color="muted">
                            Workspace: {integration.provider_workspace_name}
                          </Text>
                        )}
                        <Text size="sm" color="muted">
                          Connected on {formatDate(integration.connected_at)}
                        </Text>
                        {integration.last_used_at && (
                          <Text size="sm" color="muted">
                            Last used: {formatDate(integration.last_used_at)}
                          </Text>
                        )}
                      </VStack>
                    )}

                    {isExpired && (
                      <Text size="sm" color="warning" style={{ marginTop: '0.5rem' }}>
                        The connection has expired. Please reconnect to continue using this integration.
                      </Text>
                    )}
                  </VStack>

                  {config.comingSoon ? (
                    <Button variant="secondary" disabled>
                      Coming Soon
                    </Button>
                  ) : isConnected ? (
                    <HStack gap="sm">
                      <Button
                        variant="secondary"
                        icon={<ArrowSync20Regular />}
                        onClick={() => handleConnect(provider)}
                        loading={connecting === provider}
                      >
                        Reconnect
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDisconnect(provider)}
                        loading={disconnecting === provider}
                      >
                        Disconnect
                      </Button>
                    </HStack>
                  ) : isExpired ? (
                    <Button
                      icon={<ArrowSync20Regular />}
                      onClick={() => handleConnect(provider)}
                      loading={connecting === provider}
                    >
                      Reconnect
                    </Button>
                  ) : (
                    <Button
                      icon={<Checkmark20Regular />}
                      onClick={() => handleConnect(provider)}
                      loading={connecting === provider}
                    >
                      Connect
                    </Button>
                  )}
                </HStack>
              </Card>
            </Animate>
          )
        })}
      </VStack>

      <Animate type="slideInUp" delay={200}>
        <Card variant="subtle" padding="lg">
          <VStack gap="sm">
            <Text weight="semibold">About Integrations</Text>
            <Text size="sm" color="muted">
              Integrations allow your team to import contacts and data from external services.
              Once connected, members of your organization can search and import users from
              connected directories when using Webba apps.
            </Text>
            <Text size="sm" color="muted">
              Only organization administrators can connect or disconnect integrations.
              All access tokens are encrypted and stored securely.
            </Text>
          </VStack>
        </Card>
      </Animate>
    </VStack>
  )
}
